import JSZip from 'jszip';
import FileSaver from 'file-saver';
import { getDateTimeFromDateTime, toastWrapper } from '@/_helpers';
import { FormPdf } from '@/_elements/FormPdf';
import { PDFDocument } from 'pdf-lib';

export const downloadBulkUserFormDataPdfs = async ({
    userFormDatas = [],
    pdfNamePrefix = '',
    singleFile = true,
    onProgress = () => {},
    setTotalCount = () => {},
}) => {
    try {
        const total = userFormDatas.length;
        setTotalCount(total);

        if (!total) {
            toastWrapper.error('No forms found to download');
            return;
        }

        const pdfName = `${pdfNamePrefix || 'Bulk'}.pdf`;

        if (singleFile) {
            const chunkSize = 50;
            const chunkBlobs = [];
            let processed = 0;

            for (let i = 0; i < userFormDatas.length; i += chunkSize) {
                const chunk = userFormDatas.slice(i, i + chunkSize);
                const chunkBlob = await FormPdf(chunk, 'combined');
                chunkBlobs.push(chunkBlob);

                processed += chunk.length;
                onProgress(processed);
                // Breathe between chunks so the browser doesn't lock up
                await new Promise((resolve) => setTimeout(resolve, 100));
            }

            const mergedPdf = await PDFDocument.create();
            for (const blob of chunkBlobs) {
                const arrayBuffer = await blob.arrayBuffer();
                const chunkPdf = await PDFDocument.load(arrayBuffer);
                const copiedPages = await mergedPdf.copyPages(chunkPdf, chunkPdf.getPageIndices());
                copiedPages.forEach((page) => mergedPdf.addPage(page));
            }

            const mergedBytes = await mergedPdf.save();
            const mergedBlob = new Blob([mergedBytes], { type: 'application/pdf' });
            return FileSaver.saveAs(mergedBlob, pdfName);
        }

        const zip = JSZip();
        const processChunkSize = 100;
        let processed = 0;
        for (let i = 0; i < userFormDatas.length; i += processChunkSize) {
            const chunk = userFormDatas.slice(i, i + processChunkSize);
            const formPdfBlobs = await Promise.all(
                chunk.map(async (userForm) => {
                    return await FormPdf(userForm);
                })
            );
            formPdfBlobs.forEach((pdfBlob, index) => {
                const globalIdx = i + index;
                const uForm = userFormDatas[globalIdx];
                const fileName = `${getDateTimeFromDateTime(uForm.updatedAt)}-${uForm.userData?.name || 'User'}-${
                    uForm.formData?.name || 'Form'
                }.pdf`;
                zip.file(fileName, pdfBlob);
            });
            processed += chunk.length;
            onProgress(processed);
            await new Promise((resolve) => setTimeout(resolve, 10));
        }

        const zipFile = await zip.generateAsync({ type: 'blob' });
        const currentDate = new Date().getTime();
        const fileName = `Bulk-${currentDate}.zip`;
        return FileSaver.saveAs(zipFile, fileName);
    } catch (error) {
        console.log('ERROR IN BULK PDF DOWNLOAD', error);
        toastWrapper.error('Failed to generate bulk PDF download.');
    }
};

export const generateSubmittedFormUrl = ({
    sortBy = { key: 'createdAt', direction: 'desc' },
    currentPage = 1,
    teamId,
    teamIds,
    formName,
    member,
    channel,
    startDate,
    endDate,
    searchTags = [],
    submissionType,
    channelIdFromUrl,
    limit,
}) => {
    let url = `/userFormData?sortBy=${sortBy.key}:${sortBy.direction}&limit=${limit || 10}&page=${currentPage}`;
    if (formName) {
        if (Array.isArray(formName) && formName.length) {
            url += `&forms=${formName.map((form) => form.value).join(',')}`;
        } else if (formName.value) {
            url += `&form=${formName.value}`;
        }
    }
    if (member?.value) url += `&user=${member.value}`;
    if (channel?.value || channelIdFromUrl) {
        url += `&chatroom=${channel?.value || channelIdFromUrl}`;
    } else if (teamId) {
        url += `&team=${teamId}`;
    } else if (teamIds) {
        url += `&teamIds=${teamIds}`;
    }
    if (startDate) url += `&startDate=${startDate}`;
    if (endDate) url += `&endDate=${endDate}`;
    if (searchTags?.length) url += `&tags=${searchTags.join(',')}`;
    if (submissionType?.value) {
        if (submissionType.value === 'urlSubmission') {
            url += `&type=normal&urlSubmission=true`;
        } else {
            url += `&submissionType=${submissionType.value}&type=normal`;
        }
    }

    return url;
};
